import { useState } from 'react'
import { Check, MapPin } from 'lucide-react'
import { LOCATIONS, suggestedExpiryDate, type Location } from '../../lib/itemDefaults'

export type UnpackRowState = {
  confirmed:    boolean
  location:     Location
  expiryDate:   string | null
  tracksExpiry: boolean
  // Receipt-scan-derived per-item fields. Not rendered in the row itself, but
  // plumbed through handleFinish so the canonical confirmation, store_prices,
  // and item_batches rows carry the actual paid price + count.
  pricePaid?:   number | null
  storeName?:   string | null
  quantity?:    number | null
}

type Props = {
  name:           string
  category:       string | null
  subtitle?:      string | null
  isLast?:        boolean
  isImpulse?:     boolean
  state:          UnpackRowState
  onToggle:       () => void
  onPatch:        (patch: Partial<UnpackRowState>) => void
}

const FRIDGE_BG  = '#F0F5EC'
const FREEZER_BG = '#EDF1F5'
const PANTRY_BG  = '#F5EFE3'
const OTHER_BG   = '#F2EFEA'

function pillBg(loc: Location) {
  return loc === 'fridge'  ? FRIDGE_BG
       : loc === 'freezer' ? FREEZER_BG
       : loc === 'pantry'  ? PANTRY_BG
       : OTHER_BG
}

export function UnpackItemRow({
  name, category, subtitle, isLast, isImpulse,
  state, onToggle, onPatch,
}: Props) {
  const [expanded, setExpanded] = useState(false)
  const locationLabel = LOCATIONS.find(l => l.id === state.location)?.label ?? 'Pantry'

  return (
    <div
      className={[
        'transition',
        state.confirmed ? 'bg-[#F4F9F5]' : 'bg-paper',
        !isLast ? 'border-b border-border-hairline' : '',
      ].join(' ')}
    >
      {/* Top row */}
      <div className="flex w-full items-center gap-3 px-[14px] py-3">
        <button
          type="button"
          onClick={onToggle}
          aria-label={state.confirmed ? `Unconfirm ${name}` : `Confirm ${name}`}
          className={
            state.confirmed
              ? 'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-forest'
              : 'h-6 w-6 flex-shrink-0 rounded-full border-[1.5px] border-[#D4C4A8] bg-paper'
          }
        >
          {state.confirmed && <Check className="h-3 w-3 text-cream" strokeWidth={2.2} />}
        </button>

        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex-1 min-w-0 text-left"
        >
          <div
            className={
              state.confirmed
                ? 'truncate font-serif text-[15px] font-medium tracking-[-0.01em] text-ink line-through decoration-ink-muted decoration-[1px]'
                : 'truncate font-serif text-[15px] font-medium tracking-[-0.01em] text-ink'
            }
          >
            {name}
            {isImpulse && (
              <span className="ml-2 text-[10px] font-sans not-italic text-terracotta font-semibold uppercase tracking-[0.06em]">
                extra
              </span>
            )}
          </div>
          {subtitle && (
            <div
              className={
                state.confirmed
                  ? 'mt-0.5 truncate text-[11px] italic text-forest'
                  : 'mt-0.5 truncate text-[11px] italic text-ink-muted'
              }
            >
              {subtitle}
            </div>
          )}
        </button>

        {/* Inline location pill */}
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1 rounded-full border border-border-sand px-2.5 py-1 text-[11px] font-medium text-ink-muted flex-shrink-0"
          style={{ backgroundColor: pillBg(state.location) }}
        >
          <MapPin className="h-[10px] w-[10px]" strokeWidth={2} />
          {locationLabel}
        </button>
      </div>

      {/* Expanded controls */}
      {expanded && (
        <div className="px-[14px] pb-3 pt-1">
          <div className="rounded-[12px] border border-border-sand bg-paper p-3">
            {/* Location row */}
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-[0.15em] font-medium text-ink-muted mb-1.5">
                Where does it go?
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {LOCATIONS.map(loc => (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() => onPatch({ location: loc.id })}
                    className={
                      state.location === loc.id
                        ? 'py-2 rounded-[10px] font-serif text-[12px] bg-forest text-forest-ink font-medium'
                        : 'py-2 rounded-[10px] font-serif text-[12px] bg-cream border border-border-sand text-ink'
                    }
                  >
                    {loc.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Expiry row */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-[0.15em] font-medium text-ink-muted">
                  Expires
                </div>
                <button
                  type="button"
                  onClick={() => onPatch({
                    tracksExpiry: !state.tracksExpiry,
                    expiryDate:   !state.tracksExpiry ? suggestedExpiryDate(category) : null,
                  })}
                  className="text-[11px] italic font-serif text-forest underline underline-offset-4 decoration-forest/30"
                >
                  {state.tracksExpiry ? "Don't track" : 'Track expiry'}
                </button>
              </div>
              {state.tracksExpiry && (
                <input
                  type="date"
                  value={state.expiryDate ?? ''}
                  onChange={e => onPatch({ expiryDate: e.target.value || null })}
                  min={new Date().toISOString().slice(0, 10)}
                  className="w-full rounded-[10px] border border-border-sand bg-cream px-3 py-2 font-serif text-[13px] text-ink focus:outline-none focus:border-forest"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
